import React, { useState, useEffect } from "react";
import { GetData, PostData, PutData, DeleteData } from "../../services/api";
import DataTable from "react-data-table-component";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  UncontrolledDropdown,
  Container,
  Row,
  Col,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Label,
  FormGroup,
  Card,
  CardBody,
  CardHeader,
  Badge,
} from "reactstrap";
import { Link, Navigate } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Loader from "../../components/Common/Loader";
import Swal from 'sweetalert2';

const SalesTeamMembers = () => {
  const [members, setMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addToTeamModal, setAddToTeamModal] = useState(false);
  const [editMemberModal, setEditMemberModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [assignmentPercentage, setAssignmentPercentage] = useState(0);
  const [isActive, setIsActive] = useState(true);

  // Check if user is admin
  const getCurrentUserRole = () => {
    try {
      const authUser = localStorage.getItem("authUser");
      if (authUser) {
        const user = JSON.parse(authUser);
        return user.role || user.name?.role || "user";
      }
    } catch (error) {
      console.error("Error getting user role:", error);
    }
    return "user";
  };

  const userRole = getCurrentUserRole();
  const isAdmin = userRole === "admin";

  const fetchMembers = async () => {
    try {
      const { data } = await GetData("/sales-team-members");
      console.log("[SalesTeamMembers] Fetched members:", data);
      console.log("[SalesTeamMembers] Members count:", data?.length || 0);
      if (data && data.length > 0) {
        console.log("[SalesTeamMembers] First member sample:", {
          userId: data[0].userId,
          userName: data[0].userName,
          userEmail: data[0].userEmail,
          teams: data[0].teams,
        });
      }
      setMembers(data || []);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching members:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar los miembros. Verifica que tengas permisos de administrador.",
        confirmButtonColor: "#E9553E",
      });
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      const { data } = await GetData("/sales-teams");
      setTeams(data || []);
    } catch (error) {
      console.error("Error fetching teams:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await GetData("/users");
      console.log("[SalesTeamMembers] Fetched users:", data);
      // Normalizar IDs a string para comparación consistente
      const normalizedUsers = (data || []).map(u => ({
        ...u,
        id: String(u.id || u._id || '').trim(),
      }));
      setUsers(normalizedUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    }
  };

  const handleAddToTeam = async () => {
    if (!selectedUserId || !selectedTeamId) {
      Swal.fire({
        icon: "warning",
        title: "Campos requeridos",
        text: "Por favor selecciona un usuario y un equipo.",
        confirmButtonColor: "#E9553E",
      });
      return;
    }

    if (assignmentPercentage < 0 || assignmentPercentage > 100) {
      Swal.fire({
        icon: "warning",
        title: "Porcentaje inválido",
        text: "El porcentaje debe estar entre 0 y 100.",
        confirmButtonColor: "#E9553E",
      });
      return;
    }

    try {
      await PostData(`/sales-teams/${selectedTeamId}/members`, {
        userId: selectedUserId,
        assignmentPercentage,
        isActive,
      });

      Swal.fire({
        icon: "success",
        title: "¡Éxito!",
        text: "Miembro agregado al equipo correctamente.",
        confirmButtonColor: "#E9553E",
      });

      setAddToTeamModal(false);
      setSelectedUserId("");
      setSelectedTeamId("");
      setAssignmentPercentage(0);
      setIsActive(true);
      fetchMembers();
    } catch (error) {
      console.error("Error adding member to team:", error);
      const errorMessage = error.response?.data?.error?.message || error.message || "Error al agregar miembro al equipo";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonColor: "#E9553E",
      });
    }
  };

  const handleEditMember = (member, team) => {
    setEditingMember({ ...member, teamId: team.teamId, teamName: team.teamName });
    setAssignmentPercentage(team.assignmentPercentage);
    setIsActive(team.isActive);
    setEditMemberModal(true);
  };

  const handleUpdateMember = async () => {
    if (!editingMember) return;

    try {
      // Necesitamos encontrar el ID del SalesTeamMember
      // Por ahora, usaremos el endpoint de actualización del equipo
      const memberData = members.find(m => m.userId === editingMember.userId);
      if (!memberData) {
        throw new Error("Miembro no encontrado");
      }

      const team = memberData.teams.find(t => t.teamId === editingMember.teamId);
      if (!team) {
        throw new Error("Equipo no encontrado");
      }

      // Necesitamos obtener el ID del SalesTeamMember
      // Esto requeriría hacer una búsqueda adicional o modificar el endpoint
      // Por ahora, mostraremos un mensaje de que esta funcionalidad está en desarrollo
      Swal.fire({
        icon: "info",
        title: "En desarrollo",
        text: "La edición de miembros se realiza desde la página de detalles del equipo.",
        confirmButtonColor: "#E9553E",
      });

      setEditMemberModal(false);
      setEditingMember(null);
    } catch (error) {
      console.error("Error updating member:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al actualizar el miembro.",
        confirmButtonColor: "#E9553E",
      });
    }
  };

  const handleRemoveFromTeam = async (userId, teamId) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta acción removerá al miembro del equipo seleccionado.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#E9553E",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, remover",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      // Necesitamos obtener el ID del SalesTeamMember
      // Por ahora, mostraremos un mensaje de que esta funcionalidad está en desarrollo
      Swal.fire({
        icon: "info",
        title: "En desarrollo",
        text: "La eliminación de miembros se realiza desde la página de detalles del equipo.",
        confirmButtonColor: "#E9553E",
      });
      fetchMembers();
    } catch (error) {
      console.error("Error removing member from team:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al remover el miembro del equipo.",
        confirmButtonColor: "#E9553E",
      });
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchMembers();
      fetchTeams();
      fetchUsers();
    }
  }, [isAdmin]);

  // Redirect if not admin (after all hooks)
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const columns = [
    {
      name: <span className="font-weight-bold fs-13">Usuario</span>,
      selector: (row) => row.userName || row.userEmail || '',
      sortable: true,
      cell: (row) => (
        <div>
          <div className="font-weight-bold">{row.userName || row.userEmail || 'Usuario sin nombre'}</div>
          <div className="text-muted small">{row.userEmail || ''}</div>
        </div>
      ),
      wrap: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Equipos</span>,
      selector: (row) => (
        <div>
          {row.teams && row.teams.length > 0 ? (
            row.teams.map((team, idx) => (
              <Badge key={idx} color={team.isActive ? "success" : "secondary"} className="me-1 mb-1">
                {team.teamName}
              </Badge>
            ))
          ) : (
            <span className="text-muted">Sin equipos</span>
          )}
        </div>
      ),
      wrap: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Porcentaje Total</span>,
      selector: (row) => `${row.totalPercentage || 0}%`,
      sortable: true,
      center: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Leads Asignados</span>,
      selector: (row) => row.totalLeadsAssigned || 0,
      sortable: true,
      center: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Acciones</span>,
      cell: (row) => (
        <UncontrolledDropdown>
          <DropdownToggle tag="button" className="btn btn-soft-secondary btn-sm">
            <i className="mdi mdi-dots-horizontal font-size-18"></i>
          </DropdownToggle>
          <DropdownMenu className="dropdown-menu-end">
            {row.teams && row.teams.length > 0 && (
              <>
                {row.teams.map((team, idx) => (
                  <DropdownItem key={idx} onClick={() => handleEditMember(row, team)}>
                    <i className="mdi mdi-pencil font-size-16 me-2"></i>
                    Editar en {team.teamName}
                  </DropdownItem>
                ))}
                <DropdownItem divider />
              </>
            )}
            <DropdownItem onClick={() => setAddToTeamModal(true)}>
              <i className="mdi mdi-plus font-size-16 me-2"></i>
              Agregar a equipo
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Miembros" breadcrumbItem="Equipos de Venta" />
          
          <Row>
            <Col lg="12">
              <Card>
                <CardHeader>
                  <Row className="align-items-center">
                    <Col>
                      <h4 className="card-title mb-0">Miembros de Equipos de Venta</h4>
                    </Col>
                    <Col className="text-end">
                      <Button
                        color="primary"
                        className="btn btn-primary"
                        onClick={() => setAddToTeamModal(true)}
                      >
                        <i className="mdi mdi-plus me-2"></i>
                        Agregar a Equipo
                      </Button>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <Loader />
                  ) : (
                    <DataTable
                      columns={columns}
                      data={members}
                      pagination
                      paginationServer={false}
                      noDataComponent="No hay miembros registrados"
                      highlightOnHover
                      pointerOnHover
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Modal para agregar miembro a equipo */}
      <Modal isOpen={addToTeamModal} toggle={() => setAddToTeamModal(false)}>
        <ModalHeader toggle={() => setAddToTeamModal(false)}>
          Agregar Miembro a Equipo
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Usuario</Label>
            <Input
              type="select"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              <option value="">Selecciona un usuario</option>
              {users.map((user) => {
                const fullName = [user.name, user.lastName].filter(Boolean).join(' ').trim();
                const displayName = fullName || user.email || user.id;
                return (
                  <option key={user.id} value={user.id}>
                    {displayName} {user.email ? `(${user.email})` : ''}
                  </option>
                );
              })}
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>Equipo</Label>
            <Input
              type="select"
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
            >
              <option value="">Selecciona un equipo</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>Porcentaje de Asignación (0-100)</Label>
            <Input
              type="number"
              min="0"
              max="100"
              value={assignmentPercentage}
              onChange={(e) => setAssignmentPercentage(parseFloat(e.target.value) || 0)}
            />
          </FormGroup>
          <FormGroup check>
            <Input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <Label check>Activo</Label>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setAddToTeamModal(false)}>
            Cancelar
          </Button>
          <Button color="primary" onClick={handleAddToTeam}>
            Agregar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal para editar miembro */}
      <Modal isOpen={editMemberModal} toggle={() => setEditMemberModal(false)}>
        <ModalHeader toggle={() => setEditMemberModal(false)}>
          Editar Miembro en {editingMember?.teamName}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Porcentaje de Asignación (0-100)</Label>
            <Input
              type="number"
              min="0"
              max="100"
              value={assignmentPercentage}
              onChange={(e) => setAssignmentPercentage(parseFloat(e.target.value) || 0)}
            />
          </FormGroup>
          <FormGroup check>
            <Input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <Label check>Activo</Label>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setEditMemberModal(false)}>
            Cancelar
          </Button>
          <Button color="primary" onClick={handleUpdateMember}>
            Guardar
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default SalesTeamMembers;

